import { FC, useContext, useEffect } from 'react'
import { IToken } from '@getgo/auth-client'
import { useLocalStorage, writeStorage } from '@rehooks/local-storage'
import { emitChange, GlobalState } from '@ucc/admin-global-state'

import { ConnectedUserContext, OrganizationContext } from 'components'
import config from 'config'
import { usePartnerFeatureFlags } from 'hooks'
import { LOGGER_SETTINGS_STORAGE_FLAG } from 'utils/constants'
import { useAccountContext } from './contexts/AccountContext'
import ContactCenterUserContext from './contexts/ContactCenterUserContext'
import FeatureFlagContext from './contexts/FeatureFlagContext'
import { AppThemeContext } from './providers/AppThemeProvider'

interface GlobalStateInitializerProps {
  locale: string
}

const GlobalStateInitializer: FC<GlobalStateInitializerProps> = ({ locale, children }) => {
  const [authToken] = useLocalStorage<IToken>(config.AUTH_TOKEN_KEY)
  const {
    orgId: selectedPbxId,
    accountKey: selectedAccountKey,
    managedAdminRoles,
    isAccountLoaded,
  } = useAccountContext()

  const connectedUser = useContext(ConnectedUserContext)
  const features = useContext(FeatureFlagContext)
  const partnerFeatures = usePartnerFeatureFlags()

  const orgContext = useContext(OrganizationContext)
  const chameleonSkin: 'gotoadmin' | 'gotoapp' | 'lastpass' = 'gotoadmin'
  const licenseDetail = orgContext?.licensesDetails
  const billingInfo = orgContext?.billingInfo

  const { userContext: contactCenterUserContext } = useContext(ContactCenterUserContext)
  const appTheme = useContext(AppThemeContext)

  // Setting the global state every time oauthToken, orgId, iAmInfos, or locale change
  useEffect(() => {
    if (connectedUser && isAccountLoaded) {
      emitChange({
        oauthToken: authToken,
        orgId: selectedPbxId,
        accountKey: selectedAccountKey,
        managedAdminRoles: managedAdminRoles || null,
        theme: appTheme.chameleonTheme,
        skin: chameleonSkin,
        locale,
        connectedUser,
        featureFlags: features,
        partnerFeatureFlags: partnerFeatures,
        contactCenterUserContext,
        guidedSetupAllStepsCompleted: false,
        license: licenseDetail,
        billingInfo,
        loginWith2FA: false,
        isBannerDisplayed: false,
        rightDrawerOpened: false,
        bannerOffset: 0,
        resolveDataResidency: null,
        cartInfo: null,
        twoFactorAuthRequired: false,
      } as GlobalState)
    }

    writeStorage(config.REBRANDING, true)
    writeStorage(LOGGER_SETTINGS_STORAGE_FLAG, JSON.stringify({ logToConsole: config.LOGGER_SETTINGS_LOG_TO_CONSOLE }))

    if (features && features.length > 0) {
      writeStorage(config.SKIN_KEY, chameleonSkin)
    }
  }, [
    authToken,
    selectedPbxId,
    selectedAccountKey,
    appTheme.chameleonTheme,
    chameleonSkin,
    locale,
    features,
    partnerFeatures,
    managedAdminRoles,
    contactCenterUserContext,
    connectedUser,
    licenseDetail,
    billingInfo,
    isAccountLoaded,
  ])

  return <>{children}</>
}

export default GlobalStateInitializer
